import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import Message from "./Message";
import MessageInput from "./MessageInput";
import InstructionsDialog from "./InstructionsDialog";
import { lastMessages } from "./lastMessages";
import { getAssistantResponse } from "./api/chatAPI";
import { getAudioBlob } from "./api/audioAPI";
import { UserOptions } from "./UserOptions";
import { FaVolumeUp, FaVolumeMute } from "react-icons/fa";
import { Message as MessageType, UserOptions as UserOptionsType, LastMessagesResponse } from '../../types/chat';

interface MessageListProps {
  conversationId?: number;
}

function MessageList({ conversationId }: MessageListProps) {
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showInstructions, setShowInstructions] = useState<boolean>(false);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState<boolean>(() => {
    // Get voice setting from localStorage or default to true
    const savedVoiceSetting: string | null = localStorage.getItem("voiceEnabled");
    return savedVoiceSetting !== null ? JSON.parse(savedVoiceSetting) : true;
  });
  const bottomRef = useRef<HTMLDivElement>(null);

  // Load user options once on mount
  useEffect(() => {
    async function fetchUserOptions(): Promise<void> {
      try {
        const userOptionsResponse: UserOptionsType = await UserOptions();
        localStorage.setItem(
          "userOptions",
          JSON.stringify(userOptionsResponse)
        );
      } catch (error) {
        console.error("Error fetching user options:", error);
      }
    }
    
    // Only fetch if not already in localStorage
    if (!localStorage.getItem("userOptions")) {
      fetchUserOptions();
    }
  }, []); // Run only once on mount

  // Load messages when conversationId changes
  useEffect(() => {
    async function fetchMessages(): Promise<void> {
      try {
        setLoading(true);
        
        console.log('[MessageList] Loading messages for conversationId:', conversationId);
        const lastMessagesResponse: LastMessagesResponse = await lastMessages(conversationId);
        
        localStorage.setItem(
          "conversation_language",
          lastMessagesResponse.latest_conversation_language
        );
        
        const serialized: MessageType[] = Array.isArray(lastMessagesResponse?.serialized_messages)
          ? lastMessagesResponse.serialized_messages
          : [];
        setMessages(serialized);

        if (serialized.length === 0) {
          setShowInstructions(true);
        } else {
          setShowInstructions(false);
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchMessages();
  }, [conversationId]); // Re-run when conversationId changes

  const closeModal = (): void => {
    setShowInstructions(false);
  };

  const toggleVoice = (): void => {
    const newVoiceState: boolean = !isVoiceEnabled;
    setIsVoiceEnabled(newVoiceState);
    localStorage.setItem("voiceEnabled", JSON.stringify(newVoiceState));
  };

  useLayoutEffect(() => {
    scrollToBottom();
  }); // Scroll to bottom whenever messages change

  const scrollToBottom = (): void => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="flex h-full flex-col md:p-10">
        <div className="flex justify-center items-center h-full">
          <div className="text-center">
            <span className="loading loading-spinner loading-lg"></span>
            <p className="mt-4 text-gray-600">Loading messages...</p>
          </div>
        </div>
      </div>
    );
  }

  const addUserMessage = async (msg: MessageType): Promise<void> => {
    try {
      const newMessages: MessageType[] = [...messages, msg];
      setMessages(newMessages);
      scrollToBottom();
      await addAssistantMessage(newMessages);
    } catch (error) {
      console.error("Error adding message:", error);
    }
  };

  const addAssistantMessage = async (messages: MessageType[]): Promise<void> => {
    try {
      const response: MessageType = await getAssistantResponse(messages);
      setMessages((prevMessages) => [...prevMessages, response]);
      scrollToBottom();
      // Only generate voice if voice is enabled
      if (isVoiceEnabled) {
        await updateMessageAudioUrl(response.message_id, response.content);
      }
    } catch (error) {
      console.error("Error adding assistant message:", error);
    }
  };

  const updateMessageAudioUrl = async (message_id: string, content: string): Promise<void> => {
    const audioBlob: Blob = await getAudioBlob(content);
    const newAudioUrl: string = URL.createObjectURL(audioBlob);
    setMessages((prevMessages) => {
      const list: MessageType[] = Array.isArray(prevMessages) ? prevMessages : [];
      return list.map((message) => {
        if (message.message_id === message_id) {
          return { ...message, audioUrl: newAudioUrl };
        }
        return message;
      });
    });
    scrollToBottom();
  };

  return (
    <div className="flex h-full flex-col md:p-10">
      <InstructionsDialog isOpen={showInstructions} closeModal={closeModal} />
      
      {/* Header with voice toggle */}
      <div className="flex justify-between items-center mb-4 px-2">
        <h2 className="text-lg font-semibold text-gray-700">Chat</h2>
        <button
          onClick={toggleVoice}
          className={`btn btn-sm ${isVoiceEnabled ? 'btn-primary' : 'btn-outline'} flex items-center gap-2`}
          title={isVoiceEnabled ? 'Disable voice generation' : 'Enable voice generation'}
        >
          {isVoiceEnabled ? (
            <>
              <FaVolumeUp size={16} />
              Voice On
            </>
          ) : (
            <>
              <FaVolumeMute size={16} />
              Voice Off
            </>
          )}
        </button>
      </div>

      <section className="overflow-y-scroll h-full">
          {messages.map((msg) => {
            return (
              <Message
                key={msg.message_id}
                role={msg.role}
                content={msg.content}
                dateTime={msg.dateTime}
                audioUrl={msg.audioUrl}
                message_id={msg.message_id}
                updateAudioUrl={updateMessageAudioUrl}
                isVoiceEnabled={isVoiceEnabled}
              />
            );
          })}
          <div ref={bottomRef} />
      </section>
      <MessageInput addUserMessage={addUserMessage} conversationId={conversationId} />
    </div>
  );
}

export default MessageList;
